import { useContext, useEffect, useMemo, useState } from 'react';
import { Table } from 'components/Table/Table';
import { useMutation, useQuery } from '@apollo/client';
import { GetInspectionDocument, GetInspectionsDocument, GetIntervalsForCraftDocument, InspectionDueType, UpdateInspectionDocument } from 'graphql/generated';
import Button from 'components/Button/Button';
import { Field, Form, Formik } from 'formik';
import { FormLabel, FullFormikInput, FullFormikTextarea } from 'components/Form/StandardForm';
import Flyout, { useFlyout } from 'components/Flyout/Flyout';
import { formatApiDate, formatApiDateUTC, formatDateChange } from 'utils/formatter';
import DatePicker from 'components/DatePicker/DatePicker';
import { Cog6ToothIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import EditInspectionFlyout from '../Edit';
import { useSession } from 'contexts';
import EditEntryFlyout from '../EditEntry';
import Toast, { ToastLength, useToast } from 'components/Toast/Toast';
import { AbilityContext } from 'contexts/AbilityContext';
import { permissionHandler } from 'utils/PermissionHandler';
import Card from 'components/Card/Card';
import { webFormat } from 'utils/statuses';
import { WalkthroughContext } from 'views/Part91/Walkthrough';
export default function ViewInspection({ id, closeFlyout } : { id: string, closeFlyout: () => void } ){
    const { data: { inspection } = {} }  = useQuery(GetInspectionDocument, { variables: { id } });
    const { craftId } = useSession();
    const { flyoutProps, closeFlyout: closeEdit, updateFlyout } = useFlyout(); 
    const { showToast, toastProps } = useToast();
    const { formStep, changeStep } = useContext(WalkthroughContext);
    const ability = useContext(AbilityContext);
    const canManageInspections = permissionHandler('Manage Inspections', ability);
    const [ updateInspection ] = useMutation(UpdateInspectionDocument, { 
        refetchQueries: [ { query: GetInspectionDocument, variables: { id } }, { query: GetInspectionsDocument, variables: { craftId }  } ],
        onCompleted: () => {
            showToast({ title: 'New Inspection Entry Added', type: ToastLength.Normal });
            closeFlyout();
        },
     });
    const { data: { craft : { normalIntervals: intervals } = {} } = {} } = useQuery(GetIntervalsForCraftDocument, { variables: { craftId } });
    const today = new Date();
    today.setHours(0, 0, 0, 0);


    const intervalName = inspection?.dueInterval ? Object.keys(inspection?.dueInterval)[0] : '';
    const intervalId = intervals?.find((interval) => interval.name === intervalName)?.id ?? '';
    const [ initialValues, setInitialValues ] = useState({
        datePerformed: today,
        lastPerformedInterval: inspection?.lastPerformedInterval?.[intervalName],
        dueInterval: '0',
        dueDate: today,
        description: '',
    });
    useEffect(() => {
        if(inspection){
            const dueEvery = intervalName && inspection?.dueInterval[intervalName]?.dueEvery;
            let daysTillDue = new Date();
            if(inspection.dueDateDaysFrequency){
                daysTillDue.setDate(daysTillDue.getDate()+Number(inspection.dueDateDaysFrequency));
            }
            if(inspection.dueDateMonthsFrequency){
                daysTillDue.setMonth(daysTillDue.getMonth()+Number(inspection.dueDateMonthsFrequency));
            }
            const dueIntervalValue = (Number(inspection?.lastPerformedInterval?.[intervalName] ?? inspection?.newestInterval?.recentIntervalLog?.endValue)+Number(dueEvery));

            setInitialValues({
                datePerformed: today,
                lastPerformedInterval: inspection?.lastPerformedInterval?.[intervalName] ?? inspection?.newestInterval?.recentIntervalLog?.endValue,
                dueInterval: isNaN(dueIntervalValue) ? '0' : dueIntervalValue.toFixed(2),
                description: '',
                dueDate: new Date(daysTillDue),
            });
        }
    }, [inspection]);
    const columns = useMemo(
    () => [
        {
            Header: 'Date',
            accessor: 'completionDate', // accessor is the "key" in the data
            className: 'w-3/12 md:text-md text-[11px]',
            Cell: ({ value }: any) => {
                return formatApiDateUTC(value);
            },
        },
        {
            Header: intervalName?.replace('Engine ', '') ?? '',
            accessor: 'intervalValues',
            className: 'w-2/12 md:text-md text-xs',
            Cell: ({ value }: any) => { 
                if(inspection?.dueMethod === InspectionDueType.Interval || inspection?.dueMethod === InspectionDueType.Both){
                    return value?.[intervalName] ?? '';
                }
                return '';

            },
        },
        {
            Header: 'Notes',
            accessor: 'description',
            className: 'w-7/12 md:text-md text-xs',
        },
    ], [inspection, intervalName] );
    return (
        <>
            <Toast {...toastProps} position='top' />
            <Flyout {...flyoutProps} />
            <div className="flex flex-col w-full items-center justify-center p-4 relative">
                <Card className="w-full mb-4">
                    <div className="flex flex-col relative">
                        <div className="flex items-center justify-between">
                            <span className='text-lg truncate font-bold'>{inspection?.name}</span>
                            {canManageInspections && <Cog6ToothIcon className='h-5 w-5 flex-shrink-0 cursor-pointer text-gray-600 hover:text-gray-800' onClick={()=>updateFlyout({ title: 'Configure Inspection', content: <EditInspectionFlyout id={id} closeFlyout={(didDelete)=>{
                                closeEdit();
                                if(didDelete){
                                    closeFlyout();
                                }
                            }}/> })} />}
                        </div>
                        <span className='text-md'>{inspection?.description}</span>
                        <span><span className='font-bold'>Type: </span>{inspection?.dueMethod === InspectionDueType.Both ? "Interval and Date" : webFormat(inspection?.dueMethod)}</span>
                        <span><span className='font-bold'>Due At: </span>{inspection?.nextDueText}</span>
                        {inspection?.airworthinessItem && (
                            <div className='flex items-center gap-1 mt-2'>
                                <CheckCircleIcon className='h-4 w-4 text-blue-800' />
                                <span className='text-xs text-blue-800 font-semibold'>Airworthiness Requirement</span>
                            </div>
                        )}
                    </div>
                </Card>   
                { canManageInspections && <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={async (values) => {
                    const interval = inspection?.newestInterval;
                    const inspectionName = inspection?.dueInterval ? Object.keys(inspection?.dueInterval)[0] : '';
                    await updateInspection({ variables: { input: { id, 
                        lastPerformed: new Date(values.datePerformed), 
                        ...(interval && { lastPerformedInterval: { [inspectionName]: values.lastPerformedInterval } }),
                        ...(inspection?.dueMethod !== InspectionDueType.Interval && { dueDate: new Date(values.dueDate) }),
                        ...(inspection?.dueMethod !== InspectionDueType.Date && { intervalDueRecords: { create: [ { dueAt: values.dueInterval, interval: { connect: { id: intervalId } } } ] } }),
                        maintenanceRecords: { create: [ { completionDate: new Date(values.datePerformed), name: "Maintenance Record", description: values.description, craft: { connect: {id: craftId} }, intervalValues: {
                            [intervalName]: values.lastPerformedInterval
                        } } ] } 
                    } } });
                    if(formStep === "setupAnnualInspection"){
                        changeStep('setupAnnualInspection', 'closeAnnualFlyout', false);
                    }
                }}>
                    {({ isSubmitting, setFieldValue, values }) => (
                        <Form className={`w-full border rounded p-8 shadow bg-white ${formStep === "setupAnnualInspection" && ' z-[102] relative'}`}>
                            <div className="flex items-center justify-between mt-[-10px] mb-5 gap-1 relative">
                                <h1 className="font-bold text-lg md:text-xl truncate">New Entry</h1>
                            </div>
                            <div className="flex w-full items-center mb-2 gap-4">
                                <div className='flex flex-col w-1/2'>
                                    <FormLabel className='mt-[-10px] text-sm font-medium text-brand'>Date Performed:</FormLabel>
                                    <Field as={DatePicker} className="w-full" name="datePerformed"  afterChange={(e) => {
                                        let dueDate = formatDateChange(e.target.value) as Date;
                                        if(inspection.dueDateDaysFrequency){
                                            dueDate.setDate(dueDate.getDate()+Number(inspection.dueDateDaysFrequency));
                                        }
                                        if(inspection.dueDateMonthsFrequency && dueDate && !isNaN(dueDate.getTime())){
                                            dueDate.setMonth(dueDate.getMonth()+Number(inspection.dueDateMonthsFrequency));
                                        }
                                        setFieldValue('dueDate', dueDate);
                                    }} />
                                </div>
                                { inspection?.dueMethod !== InspectionDueType.Date && <FullFormikInput name="lastPerformedInterval" onChange={(e) =>{
                                    setFieldValue('lastPerformedInterval', e.target.value);
                                    setFieldValue('dueInterval', (Number(e.target.value)+Number(inspection?.dueInterval[intervalName]?.dueEvery)).toString());
                                }} label={`${intervalName?.replace('Engine ', '')} Performed:`} autoComplete="off" className='flex flex-col w-1/2' /> }
                            </div>
                            <FullFormikTextarea name="description" label="Notes" autoComplete="off" className='flex flex-col w-full mb-2' />
                            <div className="flex w-full items-center mb-2 gap-4">
                                { inspection?.dueMethod !== InspectionDueType.Interval && inspection?.doesRepeat !== false && 
                                    <div className='flex flex-col w-1/2 mt-[-10px]'>
                                        <FormLabel className='mt-[-1px] text-sm font-medium text-brand'>Next Date Due:</FormLabel>
                                        <Field as={DatePicker} className="w-full" name="dueDate" />
                                    </div>
                                }
                                { inspection?.dueMethod !== InspectionDueType.Date && 
                                    <div className='flex flex-col w-1/2'>
                                        <FullFormikInput name="dueInterval" label={`Next ${intervalName?.replace('Engine ', '')} Due:`} autoComplete="off" className='flex flex-col ml-1' />
                                    </div>
                                }
                            </div>
                            <hr className="my-2" />
                            <div className="flex items-center justify-end">
                                <Button text="Save New Entry" color="navy" type='submit' size="xs" />
                            </div>
                        </Form>
                    )}
                </Formik> }
                <div className="w-full border rounded p-8 shadow bg-white mt-4">
                    <h1 className="font-bold mb-4">Previous Entries</h1>
                    {inspection?.maintenanceRecords && inspection.maintenanceRecords.length > 0 ? (
                        <Table className="border w-full text-sm" columns={columns} data={[...inspection?.maintenanceRecords ?? []]?.sort((record) => record?.completionDate)} onRowClick={(row) => { 
                            canManageInspections && updateFlyout( { title: "Edit Entry", content: <EditEntryFlyout closeFlyout={closeEdit} id={ row.id } inspection={inspection} /> } );
                        } } />
                    ) : (
                        <p className="text-gray-500 text-center py-8">No previous entries</p>
                    )}
                </div>
                {inspection?.faaInfo && <Card title="FAA AD Info" className="w-full mb-4">
                    <div className="flex flex-col">
                        <span><span className='font-bold'>Ad Number: </span>{inspection?.faaInfo?.adNumber}</span>
                        {inspection?.faaInfo?.link && <span><span className='font-bold'>Link: </span><a href={inspection?.faaInfo?.link} className='text-blue-500 underline' target="_blank" rel="noreferrer">{inspection?.faaInfo?.title}</a></span>}
                        {inspection?.faaInfo?.description && <span><span className='font-bold'>Description: </span>{inspection?.faaInfo?.description}</span>}
                        <span><span className='font-bold'>Effective Date: </span>{formatApiDate(inspection?.faaInfo?.effectiveDate)}</span>
                    </div>
                </Card> }
            </div>
        </>
    );
}
